import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORES = {
  spotify: { name: "Spotify", color: "#1DB954" },
  apple: { name: "Apple Music", color: "#FA243C" },
  itunes: { name: "iTunes Store", color: "#A855F7" },
  youtube: { name: "YouTube Music", color: "#FF0000" },
  amazon: { name: "Amazon Music", color: "#25D1DA" },
  tidal: { name: "Tidal", color: "#ffffff" },
};

const FALLBACK_GRADIENTS = [
  "linear-gradient(160deg, rgba(124,58,237,.9), rgba(5,5,8,1) 75%)",
  "linear-gradient(135deg, rgba(34,211,238,.6), rgba(5,5,8,1) 80%)",
  "radial-gradient(circle at 30% 20%, rgba(168,85,247,.6), rgba(5,5,8,1) 70%)",
  "linear-gradient(200deg, rgba(250,36,60,.6), rgba(5,5,8,1) 85%)",
];

export default function SmartLinkPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [gradIdx, setGradIdx] = useState(0);

  useEffect(() => {
    fetch(`${API}/smartlink/${slug}`)
      .then((r) => { if (!r.ok) throw new Error("nf"); return r.json(); })
      .then((d) => {
        setData(d);
        document.title = `${d.title} — ${d.artist} | Smart Link`;
        fetch(`${API}/smartlink/${slug}/click`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ platform: "page_view", ref: document.referrer || "direct" }),
        }).catch(() => {});
      })
      .catch(() => setError(true));
  }, [slug]);

  const gradients = useMemo(() => {
    if (data && data.gradients && data.gradients.length) return data.gradients;
    if (data && data.gradient) return [data.gradient, ...FALLBACK_GRADIENTS];
    return FALLBACK_GRADIENTS;
  }, [data]);

  const trackClick = (platform, url) => {
    fetch(`${API}/smartlink/${slug}/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform, ref: document.referrer || "direct" }),
    }).catch(() => {});
    window.open(url, "_blank", "noopener");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#050508] text-white flex items-center justify-center p-6">
        <div className="text-center" data-testid="smartlink-error">
          <h1 className="text-2xl font-bold mb-2">Smart Link non trovato</h1>
          <p className="text-slate-400 text-sm">Il link che hai seguito non esiste o è stato disattivato.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050508] text-slate-400 flex items-center justify-center">
        <p className="text-sm tracking-widest uppercase">Caricamento…</p>
      </div>
    );
  }

  const linksObj = data.links || {};
  const storeKeys = Object.keys(linksObj).filter((k) => linksObj[k] && linksObj[k].url);

  return (
    <div
      className="min-h-screen text-white flex items-center justify-center p-4 transition-all duration-700"
      style={{ background: gradients[gradIdx] || FALLBACK_GRADIENTS[0] }}
      data-testid="smartlink-page"
    >
      {/* pulsante cambio sfondo: cicla le 4 combinazioni */}
      <button
        type="button"
        onClick={() => setGradIdx((gradIdx + 1) % gradients.length)}
        data-testid="smartlink-gradient-switch"
        className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-black/50 backdrop-blur-md text-xs font-semibold tracking-wider uppercase hover:bg-black/70 transition-all"
      >
        <span className="flex gap-1">
          {gradients.map((g, i) => (
            <span
              key={i}
              className={`w-3.5 h-3.5 rounded-full border ${i === gradIdx ? "border-white" : "border-white/30"}`}
              style={{ background: g }}
            />
          ))}
        </span>
        Sfondo
      </button>

      <div className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-black/55 backdrop-blur-xl">
        {/* artista + cover in alto al centro */}
        <div className="relative pt-8 pb-4 px-6 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Nuova uscita</p>
          <h2 className="text-lg font-bold tracking-wide mb-4" data-testid="smartlink-artist">{data.artist}</h2>
          <div className="relative mx-auto w-56 h-56 sm:w-64 sm:h-64">
            {data.artwork && (
              <>
                <div className="absolute inset-0 scale-110 blur-2xl opacity-60 rounded-2xl" style={{ backgroundImage: `url(${data.artwork})`, backgroundSize: "cover" }} />
                <img
                  src={data.artwork}
                  alt={data.title}
                  className="relative w-full h-full object-cover rounded-2xl shadow-2xl ring-1 ring-white/20"
                  data-testid="smartlink-artwork"
                />
              </>
            )}
          </div>
        </div>

        <div className="px-6 pb-6 text-center">
          <h1 className="text-2xl font-black tracking-tight mb-1" data-testid="smartlink-title">{data.title}</h1>
          {data.description && <p className="text-xs text-white/60 mb-5">{data.description}</p>}

          {data.audio_preview && (
            <div className="mb-6 rounded-xl bg-white/5 border border-white/10 p-3" data-testid="smartlink-audio">
              <audio
                controls
                preload="none"
                className="w-full h-9"
                src={data.audio_preview}
                onPlay={() => trackClick("audio_preview", data.audio_preview)}
              />
              <p className="text-[10px] text-white/40 mt-1 tracking-wider uppercase">Anteprima 30 secondi</p>
            </div>
          )}

          <div className="flex flex-col gap-2.5 mt-5" data-testid="smartlink-links">
            {storeKeys.length === 0 && <p className="text-white/50 text-sm">Nessuno store collegato.</p>}
            {storeKeys.map((key) => {
              const store = STORES[key] || { name: linksObj[key].name || key, color: "#8b5cf6" };
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`smartlink-store-${key}`}
                  onClick={() => trackClick(key, linksObj[key].url)}
                  className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-white/15 bg-white/10 hover:bg-white/20 hover:border-white/30 transition-all font-semibold text-sm"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: store.color }} />
                    {store.name}
                  </span>
                  <span className="text-white/60 text-xs tracking-wider uppercase">Ascolta ›</span>
                </button>
              );
            })}
          </div>

          <p className="mt-6 pt-4 border-t border-white/10 text-[10px] text-white/40">
            Powered by <a href="/" className="text-violet-300 hover:underline">La Palma Ent.</a> · Smart Link
          </p>
        </div>
      </div>
    </div>
  );
}
